import { useState, useEffect } from "react";
import {
  FiX,
  FiActivity,
  FiInfo,
  FiClock,
  FiZap,
  FiImage,
  FiDatabase,
  FiLayers,
  FiTag,
  FiCpu,
  FiServer,
} from "react-icons/fi";
import { useContext } from "react";
import { BaseContext } from "../../../contexts/BaseContext";
import {
  deasyWorkflowService,
  connectorService,
  graphService,
} from "../../../services/api";
import { auth } from "../../../config/firebase";
import {
  OCR_PREPARE,
  OCR_PREPARE_SCHEMA,
  PREPARE_TAG,
  PREPARE,
  CLASSIFY,
} from "./PreDefinedWorkflows";
import { SelectField } from "./Jobs/JobField";

export const WorkflowDialog = ({
  setOpenDialog,
  selectedWorkflow,
  setWorkflows,
  fetchWorkflows,
}) => {
  const { deasyApiKey } = useContext(BaseContext);
  const [isLoading, setIsLoading] = useState(false);
  const [showTemplates, setShowTemplates] = useState(!selectedWorkflow);
  const [connectors, setConnectors] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [selectedConnector, setSelectedConnector] = useState("");
  const [unstructuredConnectorSelected, setUnstructuredConnectorSelected] =
    useState(false);
  const [selectedSchema, setSelectedSchema] = useState("");

  // Form State
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    cadence: "0 0 * * *", // Default daily at midnight
    stages: [],
    status: "pending",
  });

  // Initialize form data when selectedWorkflow changes

  // Get profile information from context
  const { vdbProfiles = {} } = useContext(BaseContext);

  // Detect if selected connector is an unstructured data source
  useEffect(() => {
    if (!selectedConnector) {
      setUnstructuredConnectorSelected(false);
      return;
    }

    const connector = vdbProfiles[selectedConnector];
    if (connector) {
      const isUnstructured = [
        "SharepointDataSourceManager",
        "S3DataSourceManager",
      ].includes(connector.type);
      setUnstructuredConnectorSelected(isUnstructured);
    }
  }, [selectedConnector, vdbProfiles]);

  // Fetch connectors and schemas when dialog opens
  useEffect(() => {
    const fetchData = async () => {
      try {
        const connectorsResponse =
          await connectorService.getVDBProfiles(deasyApiKey);
        if (connectorsResponse?.data?.connectors) {
          setConnectors(connectorsResponse.data.connectors);
        }

        const schemasResponse = await graphService.getGraphs(deasyApiKey);
        if (schemasResponse) {
          setSchemas(schemasResponse);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }
    };

    fetchData();
  }, [deasyApiKey]);

  const handleSaveWorkflow = async () => {
    try {
      setIsLoading(true);

      // Create workflow data with user_id
      const workflowData = { ...formData };

      // Set user_id to current user's email when creating a new workflow
      if (!selectedWorkflow) {
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.email) {
          workflowData.user_id = currentUser.email;
        }
      }

      // Call the API to save the workflow
      const response = await deasyWorkflowService.upsertDeasyWorkflow(
        deasyApiKey,
        workflowData,
      );

      // Update the workflows list with the response data
      if (response && response.data) {
        const savedWorkflow = response.data;

        if (selectedWorkflow) {
          // Update existing workflow in the list
          setWorkflows((prevWorkflows) =>
            prevWorkflows.map((wf) =>
              wf.id === savedWorkflow.id ? savedWorkflow : wf,
            ),
          );
        } else {
          // Add new workflow to the list
          setWorkflows((prevWorkflows) => [...prevWorkflows, savedWorkflow]);
        }

        // Refresh workflows from the server to ensure latest state
        if (fetchWorkflows) {
          fetchWorkflows();
        }

        // Close dialog passing true to refresh workflows
        setOpenDialog(true);
      }
    } catch (error) {
      console.error("Failed to save workflow:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  const handleSelectTemplate = (template) => {
    if (!selectedConnector) {
      return; // Must select a connector first
    }

    let workflowTemplate;

    switch (template) {
      case "ocr-prepare":
        workflowTemplate = OCR_PREPARE(selectedConnector);
        break;
      case "ocr-prepare-schema":
        if (!selectedSchema) return; // Must select a schema
        workflowTemplate = OCR_PREPARE_SCHEMA(
          selectedConnector,
          selectedSchema,
        );
        break;
      case "prepare-tag":
        if (!selectedSchema) return; // Must select a schema
        workflowTemplate = PREPARE_TAG(selectedConnector, selectedSchema);
        break;
      case "prepare":
        workflowTemplate = PREPARE(selectedConnector);
        break;
      case "classify":
        if (!selectedSchema) return; // Must select a schema
        workflowTemplate = CLASSIFY(selectedConnector, selectedSchema);
        break;
      default:
        return;
    }

    setFormData(workflowTemplate);
    setShowTemplates(false);
  };

  // Input handlers
  const handleNameChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, name: value }));
  };

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, description: value }));
  };

  const handleCadenceChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, cadence: value }));
  };

  // Template selection component
  const TemplateSelector = () => (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-3">
          Select Workflow Template
        </h3>
        <p className="text-gray-600 mb-4">
          Choose a pre-defined workflow template to get started quickly. All
          templates are configured to run at midnight daily by default.
        </p>

        <div className="mb-5 group/field transition-all duration-200">
          <label
            htmlFor="connector-select"
            className="block mb-2 font-medium text-gray-700 group-hover/field:text-primary transition-colors duration-200 flex items-center"
          >
            <FiDatabase className="mr-1.5 text-primary/70" />
            Data Connector
          </label>
          <SelectField
            id="connector-select"
            value={selectedConnector}
            onChange={(value) => setSelectedConnector(value)}
            isDisabled={false}
            placeholder="Select a Data Connector"
            options={Object.keys(connectors).map((connectorName) => ({
              value: connectorName,
              label: connectorName,
            }))}
          />
        </div>

        <div className="mb-5 group/field transition-all duration-200">
          <label
            htmlFor="schema-select"
            className="block mb-2 font-medium text-gray-700 group-hover/field:text-primary transition-colors duration-200 flex items-center"
          >
            <FiTag className="mr-1.5 text-primary/70" />
            Tag Schema (optional for some workflows)
          </label>
          <SelectField
            id="schema-select"
            value={selectedSchema}
            onChange={(value) => setSelectedSchema(value)}
            isDisabled={false}
            placeholder="Select a Schema"
            options={schemas
              .filter((schema) => schema.graph_name !== "No schema selected")
              .map((schema) => ({
                value: schema.graph_name,
                label: schema.graph_name,
              }))}
          />
        </div>
      </div>

      <div className="grid gap-3 mt-4">
        <h4 className="text-md font-medium text-gray-700 mb-1">
          Available Templates:
        </h4>

        <button
          onClick={() => handleSelectTemplate("prepare-tag")}
          disabled={!selectedConnector || !selectedSchema}
          className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-all disabled:opacity-50 disabled:hover:border-gray-200 disabled:hover:bg-white"
        >
          <div className="p-2 bg-purple-100 text-purple-700 rounded-lg mr-4">
            <FiTag className="w-6 h-6" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-800">
              Deasy Prepare + Classification
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Run data preparation and classify according to schema
            </p>
          </div>
        </button>

        <button
          onClick={() => handleSelectTemplate("prepare")}
          disabled={!selectedConnector}
          className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-all disabled:opacity-50 disabled:hover:border-gray-200 disabled:hover:bg-white"
        >
          <div className="p-2 bg-indigo-100 text-indigo-700 rounded-lg mr-4">
            <FiServer className="w-6 h-6" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-800">Deasy Prepare</h3>
            <p className="text-sm text-gray-600 mt-1">
              Run Deasy data preparation on your content
            </p>
          </div>
        </button>

        <button
          onClick={() => handleSelectTemplate("classify")}
          disabled={!selectedConnector || !selectedSchema}
          className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-all disabled:opacity-50 disabled:hover:border-gray-200 disabled:hover:bg-white"
        >
          <div className="p-2 bg-amber-100 text-amber-700 rounded-lg mr-4">
            <FiCpu className="w-6 h-6" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-800">Classification</h3>
            <p className="text-sm text-gray-600 mt-1">
              Classify content according to selected schema
            </p>
          </div>
        </button>

        <button
          onClick={() => handleSelectTemplate("ocr-prepare")}
          disabled={!unstructuredConnectorSelected}
          className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-all disabled:opacity-50 disabled:hover:border-gray-200 disabled:hover:bg-white"
        >
          <div className="p-2 bg-blue-100 text-blue-700 rounded-lg mr-4">
            <FiImage className="w-6 h-6" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-800">OCR + Deasy Prepare</h3>
            <p className="text-sm text-gray-600 mt-1">
              Extract text with OCR and run Deasy data preparation
            </p>
          </div>
        </button>

        <button
          onClick={() => handleSelectTemplate("ocr-prepare-schema")}
          disabled={!unstructuredConnectorSelected || !selectedSchema}
          className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-all disabled:opacity-50 disabled:hover:border-gray-200 disabled:hover:bg-white"
        >
          <div className="p-2 bg-green-100 text-green-700 rounded-lg mr-4">
            <FiLayers className="w-6 h-6" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-800">
              OCR + Deasy Prepare + Classification
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Extract text with OCR, run Deasy data preparation, and classify
              according to the selected schema
            </p>
          </div>
        </button>
      </div>

      <div className="mt-8 pt-6 border-t border-gray-200">
        <button
          className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
          onClick={() => setShowTemplates(false)}
        >
          Skip & Create Custom Workflow
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 animate-fadeIn">
      <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl animate-slideIn overflow-hidden">
        <div className="flex justify-between items-center p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center">
            <div className="p-2 bg-primary/10 rounded-lg text-primary mr-3">
              <FiActivity className="w-5 h-5" />
            </div>
            <h3 className="text-xl font-bold text-gray-800">
              {selectedWorkflow
                ? "Edit Workflow"
                : showTemplates
                  ? "Create From Template"
                  : "Create New Workflow"}
            </h3>
          </div>
          <button
            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-200/70 transition-all"
            onClick={handleClose}
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {showTemplates ? (
          <TemplateSelector />
        ) : (
          <div>
            <div className="p-6">
              <div className="mb-5 group/field transition-all duration-200">
                <label
                  htmlFor="workflow-name"
                  className="block mb-2 font-medium text-gray-700 group-hover/field:text-primary transition-colors duration-200 flex items-center"
                >
                  <FiActivity className="mr-1.5 text-primary/70" />
                  Workflow Name
                </label>
                <input
                  id="workflow-name"
                  type="text"
                  value={formData.name}
                  onChange={handleNameChange}
                  placeholder="Enter workflow name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary hover:border-primary/50 transition-colors duration-200 shadow-sm"
                />
              </div>
              <div className="mb-5 group/field transition-all duration-200">
                <label
                  htmlFor="workflow-description"
                  className="block mb-2 font-medium text-gray-700 group-hover/field:text-primary transition-colors duration-200 flex items-center"
                >
                  <FiInfo className="mr-1.5 text-primary/70" />
                  Description
                </label>
                <textarea
                  id="workflow-description"
                  value={formData.description}
                  onChange={handleDescriptionChange}
                  rows={3}
                  placeholder="Describe what this workflow does"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary hover:border-primary/50 transition-colors duration-200 shadow-sm"
                />
              </div>
              <div className="mb-1 group/field transition-all duration-200">
                <label
                  htmlFor="workflow-cadence"
                  className="block mb-2 font-medium text-gray-700 group-hover/field:text-primary transition-colors duration-200 flex items-center"
                >
                  <FiClock className="mr-1.5 text-primary/70" />
                  Schedule (Cron Expression)
                </label>
                <input
                  id="workflow-cadence"
                  type="text"
                  value={formData.cadence}
                  onChange={handleCadenceChange}
                  placeholder="0 0 * * *"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary hover:border-primary/50 transition-colors duration-200 shadow-sm"
                />
                <div className="mt-2 p-3 bg-primary/5 rounded-lg border border-primary/20 text-sm text-gray-600 flex items-center justify-center">
                  <div className="flex flex-col text-center">
                    <p className="font-medium text-primary mb-0.5">Format:</p>
                    <p>minute hour day-of-month month day-of-week</p>
                    <p className="mt-1">
                      Example: '0 0 * * *' = daily at midnight
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-between gap-3 p-5 border-t border-gray-200 bg-gray-50">
              {!selectedWorkflow && formData.stages.length === 0 && (
                <button
                  className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 font-medium transition-colors shadow-sm hover:shadow flex items-center"
                  onClick={() => setShowTemplates(true)}
                >
                  <FiZap className="mr-2" /> Use Template
                </button>
              )}
              <div className="flex gap-3 ml-auto">
                <button
                  className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 font-medium transition-colors shadow-sm hover:shadow"
                  onClick={handleClose}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  className="px-5 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark font-medium transition-colors shadow-md hover:shadow-lg disabled:opacity-70 flex items-center justify-center min-w-[120px]"
                  onClick={() => handleSaveWorkflow()}
                  disabled={isLoading || !formData.name}
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {selectedWorkflow ? "Updating..." : "Creating..."}
                    </>
                  ) : selectedWorkflow ? (
                    "Update Workflow"
                  ) : (
                    "Create Workflow"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
